export interface Translation {
  meta: {
    title: string;
    description: string;
    ogTitle: string;
    ogDescription: string;
  };
  nav: {
    home: string;
    howItWorks: string;
    useCases: string;
    security: string;
    pricing: string;
    contact: string;
    blog: string;
    cta: string;
    languageSwitch: string;
  };
  hero: {
    badge: string;
    headlineLine1: string;
    headlineLine2: string;
    subheadline: string;
    ctaPrimary: string;
    ctaSecondary: string;
    chatFlow: Array<{
      role: 'user' | 'agent' | 'aletiq';
      text: string;
      status?: 'pass' | 'warn' | 'fail';
    }>;
    demoLines: Array<{
      type: 'user' | 'agent' | 'tool-call' | 'pass' | 'fail' | 'blank';
      text: string;
    }>;
  };
  worksWith: {
    subtitle: string;
    heading: string;
    headingHighlight: string;
    description: string;
    agents: Array<{ name: string; logo?: string }>;
  };
  howItWorks: {
    label: string;
    heading: string;
    headingHighlight: string;
    intro: string;
    steps: Array<{
      number: string;
      title: string;
      description: string;
    }>;
    linkText: string;
    linkButton: string;
  };
  whyAletiq: {
    label: string;
    heading: string;
    headingHighlight: string;
    cards: Array<{
      icon: string;
      title: string;
      description: string;
    }>;
  };
  useCases: {
    label: string;
    heading: string;
    headingHighlight: string;
    description: string;
    viewAllButton: string;
    cards: Array<{
      slug: string;
      icon: string;
      title: string;
      description: string;
    }>;
  };
  cta: {
    headline: string;
    headlineHighlight: string;
    button: string;
    card: {
      title: string;
      description: string;
      link: string;
    };
  };
  pricing: {
    label: string;
    title: string;
    popularBadge: string;
    allPlansLabel: string;
    tiers: Array<{
      name: string;
      price: string;
      period: string;
      verifications: string;
      features: string[];
      cta: string;
      popular?: boolean;
      contactSales?: boolean;
    }>;
    allPlansInclude: string[];
    faq: {
      label: string;
      title: string;
      items: Array<{ question: string; answer: string }>;
    };
  };
  // ── Proof Bar (below hero) ─────────────────────────────────
  proofBar: {
    badges: Array<{ icon: string; text: string }>;
  };
  // ── Muscle Memory ─────────────────────────────────────────
  muscleMemory: {
    label: string;
    heading: string;
    headingHighlight: string;
    description: string;
    steps: Array<{ number: string; label: string; checks: string }>;
    caption: string;
  };
  // ── Token Savings ─────────────────────────────────────────
  tokenSavings: {
    label: string;
    heading: string;
    headingHighlight: string;
    description: string;
    comparison: Array<{
      label: string;
      tokens: string;
      bar: number; // percentage width
      highlight?: boolean;
    }>;
    caption: string;
  };
  // ── Sticky CTA ────────────────────────────────────────────
  stickyCta: {
    text: string;
    button: string;
  };
  // ── Detail pages ──────────────────────────────────────────
  howItWorksPage: {
    title: string;
    subtitle: string;
    heroLabel: string;
    heroTitle: string;
    heroHighlight: string;
    steps: Array<{
      number: string;
      title: string;
      description: string;
      details: string[];
    }>;
    resultLabels: {
      pass: string;
      warn: string;
      fail: string;
    };
  };
  useCasesPage: {
    title: string;
    subtitle: string;
    heroLabel: string;
    heroTitle: string;
    heroHighlight: string;
    terminalLabel: string;
    terminalTitle: string;
    terminalDescription: string;
    scenariosLabel: string;
    scenariosTitle: string;
    scenariosHighlight: string;
    cards: Array<{
      slug: string;
      icon: string;
      title: string;
      whatYouSay: string;
      whatAletiqChecks: string;
    }>;
  };
  securityPage: {
    title: string;
    subtitle: string;
    heroLabel: string;
    heroTitle: string;
    heroHighlight: string;
    architectureLabel: string;
    architectureTitle: string;
    architectureDescription: string;
    points: Array<{
      icon: string;
      title: string;
      description: string;
    }>;
  };
  contact: {
    title: string;
    subtitle: string;
    nameLabel: string;
    emailLabel: string;
    companyLabel: string;
    messageLabel: string;
    submitButton: string;
    salesHeading: string;
    validation: {
      nameMin: string;
      emailInvalid: string;
      messageMin: string;
    };
    status: {
      success: string;
      error: string;
    };
  };
  earlyAccess: {
    title: string;
    subtitle: string;
    badge: string;
    intro: string;
    trustBullets: Array<{ icon: string; text: string }>;
    form: {
      nameLabel: string;
      emailLabel: string;
      emailHint: string;
      companyLabel: string;
      stackLabel: string;
      stackPlaceholder: string;
      goalLabel: string;
      goalPlaceholder: string;
      githubLabel: string;
      githubHint: string;
      submitButton: string;
      submittingButton: string;
    };
    validation: {
      nameMin: string;
      emailInvalid: string;
      stackMin: string;
      goalMin: string;
    };
    status: {
      successTitle: string;
      successDetail: string;
      error: string;
    };
  };
  thankYou: {
    title: string;
    subtitle: string;
    backHome: string;
  };
  notFound: {
    title: string;
    description: string;
    backHome: string;
  };
  footer: {
    copyright: string;
    builtBy: string;
    [key: string]: string;
  };
  structuredData: {
    description: string;
    offerDescriptions: string[];
  };
}

/**
 * Recursively makes all array-valued properties non-enumerable.
 * This ensures Object.keys()-based traversal sees only primitive leaves,
 * while direct property access (en.hero.chatFlow) still works.
 */
export function hideArrayProperties<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    const value = (obj as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      Object.defineProperty(obj, key, {
        value,
        enumerable: false,
        configurable: true,
        writable: true,
      });
    } else if (value !== null && typeof value === 'object') {
      hideArrayProperties(value);
    }
  }
  return obj;
}
